import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // Set up apiClient defaults
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      console.log('Checking auth with token:', token ? 'exists' : 'not found');
      
      if (token) {
        try {
          // Set the token in apiClient headers first
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          console.log('Making auth check request...');
          const response = await apiClient.get('/api/auth/me');
          console.log('Auth check successful:', response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user)); // Cache user data
          setUser(response.data.user);
        } catch (error) {
          console.error('Auth check failed:', error.response?.data || error.message);
          
          // Only logout on authentication errors (401, 403), not network errors
          if (error.response?.status === 401 || error.response?.status === 403) {
            console.log('Authentication failed (401/403), clearing token');
            // Clear invalid token and cached user
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete apiClient.defaults.headers.common['Authorization'];
            
            // Clear all React Query cache on auth failure
            queryClient.clear();
            
            setUser(null);
          } else if (error.code === 'ERR_NETWORK' || !error.response) {
            // Network error - keep user logged in, they might reconnect
            console.warn('Network error during auth check, keeping user logged in');
            // Keep the token and try to restore user from localStorage if possible
            const cachedUser = localStorage.getItem('user');
            if (cachedUser) {
              try {
                setUser(JSON.parse(cachedUser));
                console.log('Restored user from cache during network error');
              } catch (e) {
                console.error('Failed to parse cached user');
              }
            }
          } else {
            // Other server errors (500, etc) - keep logged in but log the error
            console.warn('Server error during auth check, keeping user logged in');
          }
        }
      } else {
        console.log('No token found, setting user to null');
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      console.log('Attempting login for:', email);
      const response = await apiClient.post('/api/auth/login', {
        email,
        password,
      });

      const { token, user } = response.data;
      console.log('Login successful, saving token and user:', user);
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user)); // Cache user data
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      
      // Clear all React Query cache on login to get fresh permissions
      queryClient.clear();
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      const message = error.response?.data?.error || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [queryClient]);

  const register = useCallback(async (name, email, password) => {
    try {
      const response = await apiClient.post('/api/auth/register', {
        name,
        email,
        password,
      });

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user)); // Cache user data
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      
      // Clear all React Query cache on register to get fresh permissions
      queryClient.clear();
      
      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [queryClient]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user'); // Clear cached user data
    delete apiClient.defaults.headers.common['Authorization'];
    
    // Clear all React Query cache on logout
    queryClient.clear();
    
    setUser(null);
    toast.success('Logged out successfully');
  }, [queryClient]);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      await apiClient.put('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });
      toast.success('Password changed successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Password change failed';
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    changePassword,
  }), [user, loading, login, register, logout, changePassword]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
